<template>
  <div id="app">
    <a class="skip-link" href="#main-content">跳至主要內容</a>
    <EnvironmentBanner />
    <MenuBar />
    <main id="main-content" tabindex="-1">
      <router-view />
    </main>
    <AmisFooter />
  </div>
</template>

<script>
import MenuBar from './components/MenuBar.vue'
import AmisFooter from './components/AmisFooter.vue'
import EnvironmentBanner from './components/EnvironmentBanner.vue'

export default {
  name: 'App',
  components: {
    MenuBar,
    AmisFooter,
    EnvironmentBanner,
  }
}
</script>

<style lang="scss">
/* CSS Variables for Design System */
:root {
  /* Color Palette */
  --primary-color: #89B1B9;
  --primary-dark: #6B8A91;
  --primary-light: #A8C5CC;
  /* 文字/白字按鈕底色專用：對白底 5.9:1；--primary-color 只用於裝飾（對白底僅 2.3:1） */
  --primary-text: #3F6B75;
  --primary-text-hover: #2C4F58;
  --focus-ring: #0068A5;
  --secondary-color: #353536;
  --accent-color: #00AFEA;
  --background-light: #F3F8F9;
  --background-white: #FFFFFF;
  --text-primary: #353536;
  --text-secondary: #666666;
  --text-light: #6E6E6E;
  
  /* Typography */
  --font-family-primary: 'Raleway Variable', 'PingFang HK', 'Microsoft JhengHei', 'Noto Sans TC', 'Source Han Sans TC', system-ui, sans-serif;
  --font-family-secondary: 'Impact', sans-serif;
  --font-size-xs: 0.75rem;
  --font-size-sm: 0.875rem;
  --font-size-base: 1rem;
  --font-size-lg: 1.125rem;
  --font-size-xl: 1.25rem;
  --font-size-2xl: 1.5rem;
  --font-size-3xl: 1.875rem;
  --font-size-4xl: 2.25rem;
  --font-size-5xl: 3rem;
  
  /* Spacing */
  --spacing-xs: 0.25rem;
  --spacing-sm: 0.5rem;
  --spacing-md: 1rem;
  --spacing-lg: 1.5rem;
  --spacing-xl: 2rem;
  --spacing-2xl: 3rem;
  --spacing-3xl: 4rem;
  
  /* Border Radius */
  --radius-sm: 0.25rem;
  --radius-md: 0.5rem;
  --radius-lg: 1rem;
  --radius-xl: 1.5rem;
  
  /* Shadows */
  --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  
  /* Transitions */
  --transition-fast: 0.15s ease;
  --transition-normal: 0.3s ease;
  --transition-slow: 0.5s ease;
}

/* Reset and Base Styles */
* {
  padding: 0;
  margin: 0;
  box-sizing: border-box;
}

html {
  scroll-behavior: smooth;
  font-size: 16px;
}

body {
  font-family: var(--font-family-primary);
  font-weight: 400;
  line-height: 1.6;
  color: var(--text-primary);
  background-color: var(--background-white);
  -webkit-tap-highlight-color: rgba(63, 107, 117, 0.15);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

h1, h2, h3 {
  text-wrap: balance;
}

p {
  text-wrap: pretty;
}

#app {
  min-height: 100vh;
  position: relative;
}

/* Container System */
.container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-lg);
}

.container-fluid {
  width: 100%;
  padding: 0 var(--spacing-lg);
}

/* Utility Classes */
.text-center { text-align: center; }
.text-left { text-align: left; }
.text-right { text-align: right; }

.flex { display: flex; }
.flex-col { flex-direction: column; }
.flex-row { flex-direction: row; }
.items-center { align-items: center; }
.justify-center { justify-content: center; }
.justify-between { justify-content: space-between; }

/* Responsive Breakpoints */
@media (max-width: 768px) {
  .container {
    padding: 0 var(--spacing-md);
  }
}

@media (max-width: 480px) {
  .container {
    padding: 0 var(--spacing-sm);
  }
}

/* Smooth Scrolling */
html {
  scroll-behavior: smooth;
}

a,
button,
[role='button'],
input,
select,
textarea,
label,
summary {
  touch-action: manipulation;
}

/* Focus Styles for Accessibility：只在键盘导航时显示焦点环 */
:focus-visible {
  outline: 3px solid var(--focus-ring);
  outline-offset: 2px;
}

#main-content:focus {
  outline: none;
}

.skip-link {
  position: fixed;
  top: 8px;
  left: 8px;
  z-index: 3000;
  padding: 0.75rem 1rem;
  background: var(--secondary-color);
  color: #fff;
  border-radius: var(--radius-md);
  text-decoration: none;
  transform: translateY(-200%);
  transition: transform 0.15s ease;

  &:focus {
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  html {
    scroll-behavior: auto;
  }

  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}

/* Selection Styles */
::selection {
  background-color: var(--primary-light);
  color: var(--text-primary);
}
</style>
